import {z} from 'zod';

import {RoleFirebaseStringSchema} from './role';

/**
 * A run of bookable office hours slots inside one section week, as hour
 * offsets from the course's `firstSectionTimestamp`. The run is the half-open
 * interval `[start, end)`: the slot at `start` is bookable, the slot at `end`
 * is not.
 */
export const OfficeHoursSlotOffsetRangeSchema = z.object({
  /** Hour offset of the first bookable slot (inclusive). */
  start: z.number().int(),
  /** Hour offset just past the last bookable slot (exclusive). */
  end: z.number().int(),
});

export type OfficeHoursSlotOffsetRange = z.infer<
  typeof OfficeHoursSlotOffsetRangeSchema
>;

/**
 * Course settings for office hours: one-hour drop-in sessions that course
 * teachers host on the course's section Zoom accounts while sections are not
 * running. Every field is optional; read through `resolveOfficeHoursConfig`
 * in `@cip/core/officeHours`, which applies the defaults.
 */
export const OfficeHoursConfigSchema = z
  .object({
    /** Whether the course runs office hours at all. Omitted means off. */
    enabled: z.boolean().optional(),
    /**
     * The lowest role that may host a session. Omitted means the default in
     * `resolveOfficeHoursConfig`.
     */
    hostMinimumRole: RoleFirebaseStringSchema.optional(),
    /**
     * Which hours of each section week may be booked. Omitted or empty
     * means every hour that keeps clear of the course's section block.
     */
    slotOffsetRanges: z.array(OfficeHoursSlotOffsetRangeSchema).optional(),
    /** First 0-based section week with office hours. Omitted means week 0. */
    firstWeekIdx: z.number().int().optional(),
    /**
     * Last 0-based section week with office hours (inclusive). Omitted means
     * the course's last section week.
     */
    lastWeekIdx: z.number().int().optional(),
    /**
     * Slots that start before this instant cannot be booked, whatever week
     * they fall in. An ISO 8601 string with a UTC offset. Omitted means no
     * such limit.
     */
    firstSlotTimestamp: z.string().optional(),
    /** How many sessions may run in the same hour. */
    maxSessionsPerSlot: z.number().int().positive().optional(),
    /** How many not-yet-held sessions one host may have booked at a time. */
    maxUpcomingPerHost: z.number().int().positive().optional(),
    /** The smallest student capacity a host may give a session. */
    minSessionCapacity: z.number().int().positive().optional(),
    /** The largest student capacity a host may give a session. */
    maxSessionCapacity: z.number().int().positive().optional(),
    /**
     * The share of a room's capacity, in percent, at which the home page
     * stops inviting students into it.
     */
    nearlyFullPercent: z.number().int().min(1).max(100).optional(),
    /** How many hosted sessions the course asks of each host. */
    requiredSessions: z.number().int().nonnegative().optional(),
    /** How far ahead of its start a slot must be booked, in minutes. */
    claimLeadMinutes: z.number().int().nonnegative().optional(),
    /**
     * How far ahead a slot opens for booking, in days: a slot can be booked
     * only once its start is this close or closer.
     */
    bookAheadDays: z.number().int().positive().optional(),
  })
  .loose();

export type OfficeHoursConfig = z.infer<typeof OfficeHoursConfigSchema>;
